import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { 
  CreditCard, 
  Shield, 
  CheckCircle, 
  AlertCircle,
  ArrowRight,
  ArrowLeft,
  Info
} from 'lucide-react';
import Header from '../common/Header';
import { useApplicationFee } from '../../hooks/useApplicationFee';

const PaymentPage = () => {
  const [loading, setLoading] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [error, setError] = useState('');
  const [orderDetails, setOrderDetails] = useState(null);
  const [pollingInterval, setPollingInterval] = useState(null);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    loadPaymentStatus();
    
    // Start polling for payment status every 10 seconds
    const interval = setInterval(() => {
      if (user?.zohoData?.applicationId || user?.zohoData?.leadId) {
        checkPaymentStatus();
      }
    }, 10000);
    
    setPollingInterval(interval);
    
    // Cleanup interval on unmount
    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [user]);

  const loadPaymentStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      const applicationId = user?.zohoData?.applicationId || user?.zohoData?.leadId;
      if (!applicationId) {
        console.log('No application ID found, skipping payment status check');
        return;
      }

      const response = await axios.get(`/api/payment/status/${applicationId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      // Normalize to the same { status, data } shape checkPaymentStatus/verifyPayment
      // use, so an already-completed payment shows the success screen immediately
      // on page load instead of waiting for the next 10-second poll.
      const fetchedStatus = response.data?.data;
      if (fetchedStatus?.paymentStatus === 'completed' || fetchedStatus?.paymentStatus === 'Completed') {
        setPaymentStatus({ status: 'completed', data: fetchedStatus });
      } else {
        setPaymentStatus(fetchedStatus);
      }
    } catch (error) {
      console.error('Error loading payment status:', error);
    }
  };

  const checkPaymentStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const applicationId = user?.zohoData?.applicationId || user?.zohoData?.leadId;
      if (!applicationId) return;

      const response = await axios.get(`/api/payment/status/${applicationId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const newStatus = response.data?.data;

      if (newStatus.paymentStatus === 'completed' || newStatus.paymentStatus === 'Completed') {
        setPaymentStatus({ status: 'completed', data: newStatus });
        if (pollingInterval) {
          clearInterval(pollingInterval);
          setPollingInterval(null);
        }
        setTimeout(() => navigate('/dashboard'), 2000);
      } else if (newStatus.paymentStatus === 'failed' || newStatus.paymentStatus === 'Failed') {
        setPaymentStatus(newStatus);
        if (pollingInterval) {
          clearInterval(pollingInterval);
          setPollingInterval(null);
        }
      } else {
        setPaymentStatus(newStatus);
      }
    } catch (error) {
      console.error('Error checking payment status:', error);
    }
  };

  const {
    classLookingForCandidates,
    classLookingFor,
    isNEETLeadersBatch,
    applicationFeeAmount,
    applicationFeeDisplay,
    feeDataLoading
  } = useApplicationFee();

  if (!feeDataLoading) {
    console.log('🔍 PaymentPage: Class-looking-for candidates from Zoho:', classLookingForCandidates);
    console.log('🔍 PaymentPage: Resolved value:', classLookingFor, '| NEET-Leaders Batch:', isNEETLeadersBatch);
  }

  const createOrder = async () => {
    if (feeDataLoading) return;

    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post('/api/payment/create-order', {
        applicationId: user?.zohoData?.applicationId || user?.zohoData?.leadId,
        amount: applicationFeeAmount,
        currency: 'INR',
        receipt: `receipt_${Date.now()}`
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = response.data;
      console.log('🔍 PaymentPage: Order creation response:', data);
      console.log('🔍 PaymentPage: Order ID:', data.data?.orderId);

      if (!data.data?.orderId) {
        setError('Order creation failed: No order ID received');
        return;
      }

      setOrderDetails(data);
      initializeRazorpay(data.data);
    } catch (error) {
      const errorData = error.response?.data;
      console.error('❌ PaymentPage: Order creation failed:', errorData);
      setError(errorData?.error || errorData?.message || 'Failed to create payment order');
    } finally {
      setLoading(false);
    }
  };

  const initializeRazorpay = (orderData) => {
    console.log('🔍 PaymentPage: Initializing Razorpay with order data:', orderData);
    console.log('🔍 PaymentPage: Order ID to use:', orderData.orderId);
    console.log('🔍 PaymentPage: Full orderData structure:', JSON.stringify(orderData, null, 2));
    
    // Validate order data
    if (!orderData || !orderData.orderId) {
      console.error('❌ PaymentPage: Invalid order data - missing orderId');
      setError('Invalid order data received. Please try again.');
      return;
    }
    
    // Check if Razorpay is loaded
    if (!window.Razorpay) {
      setError('Razorpay payment gateway is not loaded. Please refresh the page and try again.');
      return;
    }

    // Check if Razorpay key is properly configured
    const razorpayKey = process.env.REACT_APP_RAZORPAY_KEY_ID;
    if (!razorpayKey || razorpayKey === 'rzp_test_YOUR_KEY' || razorpayKey.includes('YOUR_KEY')) {
      setError('Razorpay API key not configured. Please set REACT_APP_RAZORPAY_KEY_ID in your environment variables.');
      return;
    }

    const options = {
      key: razorpayKey,
      amount: orderData.amount,
      currency: orderData.currency,
      name: 'Academic City School',
      description: 'Application Fee',
      order_id: orderData.orderId,
      handler: function (response) {
        console.log('🔍 PaymentPage: Razorpay handler called with response:', response);
        console.log('🔍 PaymentPage: Response type:', typeof response);
        console.log('🔍 PaymentPage: Response keys:', Object.keys(response));
        console.log('🔍 PaymentPage: Full response object:', JSON.stringify(response, null, 2));
        verifyPayment(response);
      },
      prefill: {
        name: user?.name || '',
        email: user?.email || '',
        contact: user?.phone || ''
      },
      theme: {
        color: '#185850'
      },
      // Required for proper verification
      modal: {
        ondismiss: function() {
          console.log('Payment modal dismissed');
        }
      },
      // Ensure all response data is included
      notes: {
        application_id: orderData.id
      },
      // Additional options for better response handling
      retry: {
        enabled: true,
        max_count: 3
      },
      // Enable external payment methods
      external: {
        wallets: ['googlepay', 'phonepe', 'paytm']
      },
      // Critical: Ensure signature generation
      payment_capture: 1,
      // Add timeout
      timeout: 300
    };

    console.log('🔍 PaymentPage: Final Razorpay options:', options);
    console.log('🔍 PaymentPage: Order ID in options:', options.order_id);

    try {
      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (error) {
      console.error('Error initializing Razorpay:', error);
      setError('Failed to initialize payment gateway. Please try again.');
    }
  };

  const verifyPayment = async (response) => {
    try {
      console.log('🔍 PaymentPage: Razorpay response object:', response);

      const token = localStorage.getItem('token');
      const verifyResponse = await axios.post('/api/payment/verify', {
        orderId: response.razorpay_order_id || response.order_id,
        paymentId: response.razorpay_payment_id || response.payment_id,
        signature: response.razorpay_signature || response.signature
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = verifyResponse.data;
      if (data.success) {
        setPaymentStatus({ status: 'completed', data: data.payment });
        setTimeout(() => navigate('/dashboard'), 2000);
      } else {
        console.error('❌ PaymentPage: Verify returned success:false:', data);
        setError('Payment verification failed');
      }
    } catch (error) {
      console.error('❌ PaymentPage: Verify request failed:', error.response?.data || error.message);
      setError(error.response?.data?.error || 'Payment verification failed');
    }
  };

  if (paymentStatus?.status === 'completed') {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header title="Payment" />
        
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-white rounded-lg shadow-sm p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-4">Payment Successful!</h1>
            <p className="text-gray-600 mb-6">
              Your application fee has been paid successfully. You can now proceed to Stage 2.
            </p>
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
              <div className="flex items-center justify-center space-x-2 text-green-800">
                <Info className="h-4 w-4" />
                <span className="text-sm">Redirecting to Stage 2 in a few seconds...</span>
              </div>
            </div>
            <button
              onClick={() => navigate('/stage2')}
              className="bg-teal-600 hover:bg-teal-700 text-white font-medium py-3 px-6 rounded-lg transition-colors duration-200"
            >
              Continue to Stage 2
              <ArrowRight className="h-4 w-4 ml-2 inline" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header title="Payment" />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Payment Header */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
          <div className="text-center">
            <h1 className="text-3xl font-bold text-gray-900 mb-4">Application Fee Payment</h1>
            <p className="text-lg text-gray-600 mb-6">
              Complete your payment to proceed to Stage 2 of the application
            </p>
            <div className="flex items-center justify-center space-x-4 text-sm text-gray-500">
              <div className="flex items-center space-x-2">
                <div className="w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center text-sm font-bold">
                  ✓
                </div>
                <span>Stage 1 Complete</span>
              </div>
              <ArrowRight className="h-4 w-4 text-gray-400" />
              <div className="flex items-center space-x-2">
                <div className="w-8 h-8 bg-teal-600 text-white rounded-full flex items-center justify-center text-sm font-bold">
                  2
                </div>
                <span>Payment</span>
              </div>
              <ArrowRight className="h-4 w-4 text-gray-400" />
              <div className="flex items-center space-x-2">
                <div className="w-8 h-8 bg-gray-200 text-gray-500 rounded-full flex items-center justify-center text-sm font-bold">
                  3
                </div>
                <span>Stage 2</span>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Payment Summary */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
                <CreditCard className="h-5 w-5 text-teal-600 mr-2" />
                Payment Summary
              </h2>
              
              <div className="space-y-4">
                <div className="flex justify-between items-center py-3 border-b border-gray-200">
                  <span className="text-gray-600">Application Fee</span>
                  {feeDataLoading ? (
                    <span className="inline-block h-5 w-16 bg-gray-200 rounded animate-pulse" />
                  ) : (
                    <span className="font-medium text-gray-900">{applicationFeeDisplay}</span>
                  )}
                </div>
                <div className="flex justify-between items-center py-4">
                  <span className="text-lg font-semibold text-gray-900">Total Amount</span>
                  {feeDataLoading ? (
                    <span className="inline-block h-7 w-20 bg-gray-200 rounded animate-pulse" />
                  ) : (
                    <span className="text-xl font-bold text-teal-600">{applicationFeeDisplay}</span>
                  )}
                </div>
              </div>

              {/* Payment Method */}
              <div className="mt-8">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Payment Method</h3>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-blue-600 rounded flex items-center justify-center">
                      <CreditCard className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">Razorpay</p>
                      <p className="text-sm text-gray-600">Secure payment gateway</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Error Message */}
              {error && (
                <div className="mt-6 bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-center space-x-3">
                    <AlertCircle className="h-5 w-5 text-red-600" />
                    <p className="text-red-800 text-sm">{error}</p>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="mt-8 flex flex-col sm:flex-row gap-4">
                <button
                  onClick={createOrder}
                  disabled={loading || feeDataLoading}
                  className="flex-1 bg-teal-600 hover:bg-teal-700 text-white font-medium py-3 px-6 rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {loading || feeDataLoading ? (
                    <div className="flex items-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      {feeDataLoading ? 'Loading fee...' : 'Processing...'}
                    </div>
                  ) : (
                    <>
                      <CreditCard className="h-4 w-4 mr-2" />
                      Pay {applicationFeeDisplay}
                    </>
                  )}
                </button>

                <button
                  onClick={() => navigate(-1)}
                  className="flex-1 border border-gray-300 text-gray-700 font-medium py-3 px-6 rounded-lg hover:bg-gray-50 transition-colors duration-200 flex items-center justify-center"
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back
                </button>
              </div>
            </div>
          </div>

          {/* Important Information */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <Info className="h-5 w-5 text-teal-600 mr-2" />
                Important Notes
              </h3>
              
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-teal-500 rounded-full mt-2"></div>
                  <p className="text-sm text-gray-600">
                    Application fee is non-refundable once paid
                  </p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-teal-500 rounded-full mt-2"></div>
                  <p className="text-sm text-gray-600">
                    Payment is required to access Stage 2 application
                  </p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-teal-500 rounded-full mt-2"></div>
                  <p className="text-sm text-gray-600">
                    All payments are processed securely via Razorpay
                  </p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-teal-500 rounded-full mt-2"></div>
                  <p className="text-sm text-gray-600">
                    You will receive a payment confirmation email
                  </p>
                </div>
              </div>

              {/* Security Badge */}
              <div className="mt-6 bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center space-x-3">
                  <Shield className="h-5 w-5 text-green-600" />
                  <div>
                    <p className="font-medium text-green-900">Secure Payment</p>
                    <p className="text-sm text-green-700">256-bit SSL encryption</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentPage;
